import { FeatureFlag } from '../api/models';

/** updatedBy value the server records when a subsystem pauses itself. */
export const AUTO_PAUSE_UPDATED_BY = 'system:auto-pause';

export interface FeatureFlagDescriptor {
  name: string;
  title: string;
  description: string;
  /**
   * 'action' flags let a subsystem act on the outside world; 'shadow' flags
   * only compute and log, so enabling them is low-risk.
   */
  kind: 'action' | 'shadow';
}

export const featureFlagCatalog: FeatureFlagDescriptor[] = [
  {
    name: 'mail_router_enabled',
    title: 'Mail Router',
    description: 'Inbound classification and routing of registrar mail.',
    kind: 'action',
  },
  {
    name: 'mail_router_shadow',
    title: 'Mail Router Shadow',
    description: 'Classify and log only — no forwards, no actions.',
    kind: 'shadow',
  },
  {
    name: 'contact_writes_enabled',
    title: 'Contact Writes',
    description: 'Registrant contact writes.',
    kind: 'action',
  },
  {
    name: 'contact_migration_shadow',
    title: 'Contact Migration Shadow',
    description: 'Compute and diff the contact write without sending it.',
    kind: 'shadow',
  },
  {
    name: 'raa_auto_verify_enabled',
    title: 'RAA Auto-Verify',
    description: 'Following the RAA verification link.',
    kind: 'action',
  },
  {
    name: 'afternic_resubmit_enabled',
    title: 'Afternic Resubmit',
    description: 'Re-listing to request Fast Transfer.',
    kind: 'action',
  },
  {
    name: 'fast_transfer_initiation_shadow',
    title: 'Fast Transfer Initiation Shadow',
    description: 'Build and log the relist payload without submitting.',
    kind: 'shadow',
  },
  {
    name: 'fast_transfer_clicker_enabled',
    title: 'Fast Transfer Clicker',
    description: 'Dispatching the browser sidecar to accept Fast Transfer terms.',
    kind: 'action',
  },
];

const catalogByName = new Map(featureFlagCatalog.map((d) => [d.name, d]));

export interface FeatureFlagGroup {
  name: string;
  descriptor?: FeatureFlagDescriptor;
  global?: FeatureFlag;
  overrides: FeatureFlag[];
}

/**
 * Groups flag rows by name in catalog order, with unknown names appended
 * alphabetically. Every catalog flag gets a group even when no row exists yet
 * (the PATCH endpoint upserts, so toggling creates the row).
 */
export function groupFeatureFlags(rows: FeatureFlag[]): FeatureFlagGroup[] {
  const byName = new Map<string, FeatureFlagGroup>();
  for (const descriptor of featureFlagCatalog) {
    byName.set(descriptor.name, { name: descriptor.name, descriptor, overrides: [] });
  }
  for (const row of rows) {
    let group = byName.get(row.name);
    if (!group) {
      group = { name: row.name, descriptor: catalogByName.get(row.name), overrides: [] };
      byName.set(row.name, group);
    }
    if (row.scope === 'account' && row.accountKey !== '') {
      group.overrides.push(row);
    } else {
      group.global = row;
    }
  }
  const groups = Array.from(byName.values());
  for (const group of groups) {
    group.overrides.sort((a, b) => a.accountKey.localeCompare(b.accountKey));
  }
  const catalogOrder = new Map(featureFlagCatalog.map((d, i) => [d.name, i]));
  groups.sort((a, b) => {
    const ai = catalogOrder.get(a.name);
    const bi = catalogOrder.get(b.name);
    if (ai !== undefined && bi !== undefined) return ai - bi;
    if (ai !== undefined) return -1;
    if (bi !== undefined) return 1;
    return a.name.localeCompare(b.name);
  });
  return groups;
}

/** Replaces the row with the same (name, scope, accountKey), or appends it. */
export function upsertFlagRow(rows: FeatureFlag[], row: FeatureFlag): FeatureFlag[] {
  const idx = rows.findIndex(
    (r) => r.name === row.name && r.scope === row.scope && r.accountKey === row.accountKey,
  );
  if (idx === -1) return [...rows, row];
  const next = rows.slice();
  next[idx] = row;
  return next;
}

export function removeOverrideRow(rows: FeatureFlag[], name: string, accountKey: string): FeatureFlag[] {
  return rows.filter(
    (r) => !(r.name === name && r.scope === 'account' && r.accountKey === accountKey),
  );
}

/** True when the row was switched off by an auto-pause trigger rather than a person. */
export function isSelfPaused(row: FeatureFlag): boolean {
  return !row.enabled && row.updatedBy === AUTO_PAUSE_UPDATED_BY;
}

/**
 * Unknown flags are treated as action-capable so enabling them still asks for
 * confirmation.
 */
export function isActionCapable(name: string): boolean {
  return catalogByName.get(name)?.kind !== 'shadow';
}
